import { useClaimedSellers } from "@/queries/customer/claimed-sellers";
import { useProductsPricing } from "@/queries/products/productsPricing";
import {
  resolveOfferPriceState,
  type OfferPriceResolver
} from "@/utils/offerPriceState";
import type { ProductStatus } from "@medusajs/types";

import type { TypesenseProduct } from "@/lib/typesense";
import { create } from "mutative";
import { useCallback, useMemo } from "react";

import { SalonStockerHttpTypes } from "@repo/api-sdk";

const DEFAULT: TypesenseProduct[] = [];

export type ProductWithAccuratePricingData = TypesenseProduct & {
  status: ProductStatus;
};

/**
 * Merges pricing data from the API into Typesense search results.
 * Uses mutative for immutable updates with structural sharing -
 * only changed objects get new references, unchanged objects preserve their identity.
 *
 * Handles:
 * - Updating existing offers with fresh pricing/status data
 * - Marking offers as "inactive" if they no longer exist in pricing response
 * - Adding new variants/offers that exist in pricing but not in Typesense
 */
export function useProductsWithAccuratePricingResults(
  items: TypesenseProduct[] = DEFAULT
) {
  const product_ids = useMemo(() => items.map((item) => item.id), [items]);

  const { data: productsPricing, status } = useProductsPricing({
    product_ids
  });

  // The offers' own prices are the search index's retail prices until pricing
  // returns, and a claimed salon must never see those (KAN-343). Callers ask
  // this per seller whether an offer may show its price yet.
  const claims = useClaimedSellers();

  // The offers pricing actually priced. One it left out (or one of a product it
  // did not publish) still holds its index price, which is no price to show.
  const pricedOfferIds = useMemo(() => {
    const ids = new Set<string>();
    for (const product of productsPricing?.products ?? []) {
      if (product.status !== ("published" satisfies ProductStatus)) continue;
      for (const variant of product.variants) {
        for (const offer of variant.seller_offers) ids.add(offer.id);
      }
    }
    return ids;
  }, [productsPricing]);

  const offerPriceState = useCallback<OfferPriceResolver>(
    (offer) =>
      resolveOfferPriceState(
        offer.seller_id,
        claims,
        status,
        pricedOfferIds.has(offer.id)
      ),
    [claims, status, pricedOfferIds]
  );

  const mergedProducts: ProductWithAccuratePricingData[] =
    useMemo((): ProductWithAccuratePricingData[] => {
      if (!productsPricing?.products) {
        // No pricing data yet, mark all as rejected
        return items.map(
          (item) =>
            ({
              ...item,
              status: "rejected" as const
            }) satisfies ProductWithAccuratePricingData
        );
      }

      // Build lookup maps for O(1) access
      const pricingByProductId = new Map(
        productsPricing.products.map((p) => [p.id, p])
      );

      // Map: offer_id -> offer from pricing API
      const offerPricingMap = new Map<
        string,
        SalonStockerHttpTypes.StoreSellerOffer
      >();
      // Map: variant_id -> variant from pricing API
      const variantPricingMap = new Map<
        string,
        SalonStockerHttpTypes.StoreProductVariantPreview
      >();

      for (const product of productsPricing.products) {
        for (const variant of product.variants) {
          variantPricingMap.set(variant.id, variant);
          for (const offer of variant.seller_offers) {
            offerPricingMap.set(offer.id, offer);
          }
        }
      }

      // Use mutative for immutable updates with structural sharing
      return create(items, (draft) => {
        for (const product of draft) {
          const pricingData = pricingByProductId.get(product.id);

          // Product completely missing in pricing response or not published - we need to mark all offers as inactive
          if (
            !pricingData ||
            pricingData.status !== ("published" satisfies ProductStatus)
          ) {
            for (const variant of product.variants) {
              for (const offer of variant.seller_offers) {
                offer.status =
                  SalonStockerHttpTypes.StoreSellerOfferStatus.INACTIVE;
              }
            }

            // Mark product as rejected (no pricing data)
            (product as any).status = "rejected";
            continue;
          }

          // Track which variant IDs exist in Typesense
          const typesenseVariantIds = new Set(
            product.variants.map((v) => v.id)
          );

          // Merge existing variants and their offers
          for (const variant of product.variants) {
            const pricingVariant = variantPricingMap.get(variant.id);

            // Track which offer IDs exist in Typesense for this variant
            const typesenseOfferIds = new Set(
              variant.seller_offers.map((o) => o.id)
            );

            // Update or mark inactive existing offers
            for (const offer of variant.seller_offers) {
              const pricingOffer = offerPricingMap.get(offer.id);

              if (pricingOffer) {
                // Offer exists in pricing - update with fresh data
                offer.status = pricingOffer.status;
                offer.handling_time = pricingOffer.handling_time;
                offer.calculated_price = pricingOffer.calculated_price;
              } else {
                // Offer no longer exists in pricing - mark as inactive
                offer.status =
                  SalonStockerHttpTypes.StoreSellerOfferStatus.INACTIVE;
              }
            }

            // Add new offers from pricing that don't exist in Typesense
            if (pricingVariant) {
              for (const pricingOffer of pricingVariant.seller_offers) {
                if (!typesenseOfferIds.has(pricingOffer.id)) {
                  variant.seller_offers.push(pricingOffer);
                }
              }
            }
          }

          // Add new variants from pricing that don't exist in Typesense
          for (const pricingVariant of pricingData.variants) {
            if (!typesenseVariantIds.has(pricingVariant.id)) {
              product.variants.push(pricingVariant);
            }
          }

          // Update meta with best seller offer from pricing data and mark as published
          (product as any).status = "published";
          (product as any).meta = pricingData.meta;
        }
      }) as ProductWithAccuratePricingData[];
    }, [items, productsPricing]);

  return {
    products: mergedProducts,
    pricingStatus: status,
    offerPriceState
  };
}
